import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { ExternalNavItem, NavItem } from './nav-item.model';
import { DividerComponent } from '@shared/components/divider.component';
import { AuthService } from '@core/auth/auth.service';
import { IframeService } from '@core/services/iframe.service';

@Component({
  selector: 'app-sidenav',
  imports: [RouterOutlet, MatButtonModule, CommonModule, RouterLinkActive, RouterLink, DividerComponent],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent {
  private router = inject(Router);
  private readonly userService = inject(AuthService);
  private readonly iframeService = inject(IframeService);

  isCollapsed = signal(false);
  itemExpanded = signal<string | undefined>(undefined);
  // Track expansion state for external nav groups (by label)
  externalItemExpanded = signal<string | undefined>(undefined);
  adminItemExpanded = signal<string | undefined>(undefined);

  isRiskManager = this.userService.isRiskManager;
  isEmbedded = this.iframeService.isEmbedded;

  navItems: NavItem[] = [
    { id: 'home', icon: 'home', label: 'Home', link: '' },
    {
      id: 'assessments',
      icon: 'library_books',
      label: 'Assessments',
      link: '/assessments',
    },
    {
      id: 'bordereau',
      icon: 'assignment',
      label: 'Bordereau Assessments',
      link: '/treaty-assessments',
    },
    {
      id: 'watchlists',
      icon: 'list_alt',
      label: 'Watchlists',
      link: '/watchlists',
      children: [
        {
          id: 'company-watchlist',
          label: 'Company Watchlist',
          link: '/watchlists/company',
          parentId: 'watchlists',
        },
        {
          id: 'project-watchlist',
          label: 'Project Watchlist',
          link: '/watchlists/project',
          parentId: 'watchlists',
        },
        {
          id: 'country-sector-watchlist',
          label: 'Country/Sector Watchlist',
          link: '/watchlists/country-sector',
          parentId: 'watchlists',
        },
      ],
    },
    {
      id: 'user-guides',
      icon: 'explore',
      label: 'User Guides',
      link: '/user-guides',
      children: [
        {
          id: 'assessment-guide',
          label: 'Assessment Guide',
          link: '/user-guides/assessment-guide',
          parentId: 'user-guides',
        },
        {
          id: 'fuzzy-name-matching-guide',
          label: 'Fuzzy Name Matching',
          link: '/user-guides/fuzzy-name-matching',
          parentId: 'user-guides',
        },
      ],
    },
  ];

  adminNavItems: NavItem[] = [
    {
      id: 'admin-companies',
      icon: 'business',
      label: 'Companies',
      link: '/companies',
      children: [
        { id: 'admin-companies-list', label: 'Companies', link: '/companies', parentId: 'admin-companies' },
        { id: 'admin-company-match', label: 'Company match', link: '/companies-match', parentId: 'admin-companies' },
      ],
    },
    { id: 'admin-projects', icon: 'folder', label: 'Projects', link: '/projects' },
    { id: 'admin-sectors', icon: 'handyman', label: 'Sectors', link: '/sectors' },
    {
      id: 'admin-country-sector',
      icon: 'public',
      label: 'Country Sector',
      link: '/country-sector',
      children: [
        { id: 'admin-cs-mappings', label: 'Mappings', link: '/country-sector', parentId: 'admin-country-sector' },
        { id: 'admin-cs-advices', label: 'Advices', link: '/country-sector-advice', parentId: 'admin-country-sector' },
      ],
    },
    { id: 'admin-managed-texts', icon: 'article', label: 'Managed Texts', link: '/managed-texts' },
    { id: 'admin-audit', icon: 'checklist', label: 'Audit log', link: '/audit' },
  ];

  externalNavItems: ExternalNavItem[] = [
    {
      icon: 'info',
      label: 'Further Information',
      children: [
        {
          label: 'SRM SharePoint',
          link: 'https://swissre.sharepoint.com/sites/ep-risk-management/SitePages/Sustainability-Risk-Management.aspx',
        },
        {
          label: 'Internal ESG Risk Framework',
          link: 'https://swissre.sharepoint.com/sites/ep-risk-management/SiteAssets/SitePages/Sustainability-Risk-Management/ESGRiskFramework_updated.pdf?CID=8b804e13-cf07-4840-b556-818660823808',
        },
      ],
    },
  ];

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        const currentUrl = this.router.url;
        const currentNavItem = this.navItems.filter(item => item.children).find(item => currentUrl.startsWith(item.link));
        if (currentNavItem) {
          this.itemExpanded.set(currentNavItem.id);
        }
        const currentAdminItem = this.adminNavItems.filter(item => item.children).find(item => currentUrl.startsWith(item.link));
        if (currentAdminItem) {
          this.adminItemExpanded.set(currentAdminItem.id);
        }
      });
  }

  updateCollapsed() {
    this.isCollapsed.update(prev => !prev);
  }

  clickOnNavItem(navItem: NavItem) {
    this.adminItemExpanded.set(undefined);
    if (navItem.children) {
      this.itemExpanded.set(navItem.id);
    } else if (this.itemExpanded() !== navItem?.parentId) {
      this.itemExpanded.set('');
    }
  }

  clickOnAdminNavItem(navItem: NavItem) {
    this.itemExpanded.set(undefined);
    if (navItem.children) {
      this.adminItemExpanded.set(navItem.id);
    } else if (this.adminItemExpanded() !== navItem?.parentId) {
      this.adminItemExpanded.set('');
    }
  }

  clickOnExternalNavItem(navItem: ExternalNavItem) {
    if (navItem.children) {
      this.externalItemExpanded.update(prev => (prev === navItem.label ? undefined : navItem.label));
    }
  }
}
